
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthResponse } from '../../types/user';
import { APIHOST } from '../../common';
import AuthDialog from './AuthDialog';
import { useNavigate } from 'react-router';
import { User } from '../../types/user';
import { Message } from '@/src/types/message';

interface AuthContextType {
  currentUser: User | null;
  isLoggedIn: boolean;
  userType: string | null;
  userToken: string | null;
  showAuthDialog: (mode?: 'login' | 'register') => void;
  hideAuthDialog: () => void;
  login: (authResponse: AuthResponse) => void;
  logout: () => void;
  loadingUser: boolean;
  unreadMessages?: Message[];
  refreshUnreadMessages: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [authDialogMode, setAuthDialogMode] = useState<'login' | 'register'>('login');
  const [loadingUser, setLoadingUser] = useState(true);
  const [unreadMessages, setUnreadMessages] = useState<Message[] | undefined>(undefined);
  const navigate = useNavigate();

  const refreshUnreadMessages = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setUnreadMessages(undefined);
      return;
    }

    try {
      const response = await fetch(`${APIHOST}/api/messages/unread`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch unread messages');
      }

      const data = await response.json();
      setUnreadMessages(data);
    } catch (error) {
      console.error('Error fetching unread messages:', error);
      setUnreadMessages(undefined);
    }
  }

  // Verify user authentication status with API
  useEffect(() => {
    
    const verifyUser = async () => {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        console.debug('No auth token found, skipping user verification');
        setLoadingUser(false);
        return;
      }

      try {
    
        const response = await fetch(`${APIHOST}/api/users/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Authentication verification failed');
        }

        // Optional: Update user data if API returns updated info
        const userData = await response.json();
        if (userData) {
          // Update currentUser if needed
          setCurrentUser(userData);
          setLoadingUser(false);
          localStorage.setItem('user', JSON.stringify(userData));
        }
      } catch (error) {
        console.error('User verification failed:', error);
        // Clear auth data and set user to null
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        setCurrentUser(null);
        setLoadingUser(false);
      }
    };

   
      verifyUser();
    
  }, []);

  const login = (authResponse: AuthResponse) => {
    setCurrentUser(authResponse.user);
    localStorage.setItem('authToken', authResponse.jwt);
    localStorage.setItem('user', JSON.stringify(authResponse.user));
    setAuthDialogOpen(false);
    if (authResponse.user.userType === 'internal') {
      console.debug('Admin user logged in:', authResponse.user);
      navigate("/admin");
    } else {
      navigate("/");
    }
  };

  const logout = () => {
    setCurrentUser(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setAuthDialogOpen(false);
    navigate("/");
  };

  const showAuthDialog = (mode: 'login' | 'register' = 'login') => {
    setAuthDialogMode(mode);
    setAuthDialogOpen(true);
  };

  const hideAuthDialog = () => {
    setAuthDialogOpen(false);
  };

  const getToken = () => {
    return localStorage.getItem('authToken');
  }

  const isLoggedIn = !!currentUser;
  const userType = currentUser?.userType || null;

  const contextValue: AuthContextType = {
    currentUser,
    isLoggedIn,
    userType,
    showAuthDialog,
    hideAuthDialog,
    login,
    logout,
    loadingUser,
    userToken: getToken(),
    unreadMessages,
    refreshUnreadMessages
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      <AuthDialog
        open={authDialogOpen}
        onClose={hideAuthDialog}
        onAuthSuccess={login}
        initialMode={authDialogMode}
      />
    </AuthContext.Provider>
  );
};